import React, { forwardRef, type ReactNode } from 'react'
import { Pressable } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { linkContract } from './link.contract'

export const LINK_VARIANT = variantForStyleId(linkContract.textStyleIds[0])

const LINK = linkContract.variants.default

export type LinkProps = Omit<React.ComponentProps<typeof Pressable>, 'style' | 'children'> & {
    children: ReactNode
    /** Figma's fifth state. CSS `:visited` only exists on an anchor, and this is not one. */
    visited?: boolean
    className?: string
}

type LinkRef = React.ComponentRef<typeof Pressable>

/**
 * Figma models Link as five states and nothing else, so folding the state axis
 * away leaves one combination — hover and pressed as CSS, disabled and visited
 * applied here because neither reaches a react-native-web div.
 */
export const Link = forwardRef<LinkRef, LinkProps>(
    ({ children, visited = false, disabled = false, className, ...rest }, ref) => (
        <Pressable
            ref={ref}
            disabled={disabled}
            role='link'
            className={twMerge(
                'flex-row items-center',
                LINK.container,
                disabled ? LINK.disabled : undefined,
                className,
            )}
            {...rest}
        >
            <Typography
                variant={LINK_VARIANT}
                className={twMerge(LINK.label, visited ? LINK.visited : undefined)}
            >
                {children}
            </Typography>
        </Pressable>
    ),
)

Link.displayName = 'Link'
